import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0113 from "../lesson-13/lesson";

const linkLesson0113 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0113.id);

const theory = ` <h3 class=sectionHead>The monopole</h3>
<p>
   A monopolar source is a source which generates divergent spherical waves. It is sometimes referred to as omnidirectional point source. This type of source may be carried out with a pulsating sphere (see <a href="${linkLesson0113}" target="_blank">Lesson 1.13</a>) whose radius tends to zero. Assuming that the monopole is characterized by its volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   radiated pressure in the frequency domain is given by
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(1)
</table>
<p>The monopole is a pulsating sphere of infinitely small radius but finite volume velocity. This is a purely theoretical object because in practice the flow is proportional to the surface of the pulsating surface. Note that for a volume velocity independant of frequency the pressure level is proportional to the frequency. Thus, to obtain a given pressure level at low frequency, increasing the flow rate implies increasing either the amplitude of the vibration of the source, or its size.
<h3 class=sectionHead>The dipole</h3>
<p>
   A dipole is made of two monopoles delivering the same volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   in phase opposition and placed at a small distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   one from each other. We consider a dipole oriented on the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   axis. The problem being rotationally symmetrical the observation point
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
   </math>
   can be put in the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>xoy</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   plan for simplicity (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-1.svg data-img-alt="Figure 1. Dipole.">Figure 1</a>).
   <figure class=figure>
<p><img alt="Figure 1. Dipole." src=${config.ASSETS_URL}/01/14/theory/figure-1.svg><figcaption class=caption>Figure 1. Dipole.</figcaption></figure>
<p>The resulting pressure is the sum of the pressures delivered by each source. So
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Note that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   do not depend only on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   but on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">r</mi>
   </math>
   that it to say more precisely on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>. Vector
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant="bold">OM</mi>
   </math>
   being given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>r</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>, vectors
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>d</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>d</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   or 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>d</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>,</mo>
      <mi>r</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math> and 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>d</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>,</mo>
      <mi>r</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>. Consequently distances
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>∕</mo>
            <mn>4</mn>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>rd</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </mrow>
      </msqrt>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>∕</mo>
            <mn>4</mn>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>rd</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </mrow>
      </msqrt>
   </math>. The expression of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   as a function of r and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   is then relatively complicated, but it simplifies considering
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mi>d</mi>
   </math>. In that case, at first order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. Moreover
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   at first order. So we have
<table class=equation>
   <tr>
      <td style="font-size: 7px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mfrac>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </mfrac>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mfrac>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </mfrac>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<p>Finally, we get
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>2</mn>
                  <mi>r</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>k</mi>
                  <mfrac>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jd</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>k</mi>
                  <mfrac>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   This expression is still relatively complicated, but it simplifies assuming
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   which means that the distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   should be small compared to the wavelength. In that case
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi>k</mi>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≈</mo>
      <mi>k</mi>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi>k</mi>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
   </math>.
<p>
   We then obtain the expression of the infinitesimal dipole <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <mo>(</mo>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mi>r</mi>
      <mo>)</mo>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>kd</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Examination of this expression highlights a number of specificity of dipole radiation. Indeed, the amplitude of the pressure of the monopole is multiplied by the term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mrow>
         <mo form=prefix fence=true>(</mo>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </mfrac>
         </mrow>
         <mo form=postfix fence=true>)</mo>
      </mrow>
   </math>
   which has a number of consequences:
   <figure class=figure>
<p><img alt="Figure 2. Directivity of the dipole (linear polar plot)." src=${config.ASSETS_URL}/01/14/theory/figure-2.svg><figcaption class=caption>Figure 2. Directivity of the dipole (linear polar plot).</figcaption></figure>
<figure class=figure><p><img alt="Figure 3. Far field frequency response of the dipole. Thin line: response of the monopole." src=${config.ASSETS_URL}/01/14/theory/figure-3.svg><figcaption class=caption>Figure 3. Far field frequency response of the dipole. Thin line: response of the monopole.</figcaption></figure>
<p>
   - Since
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>, the dipole radiation is considerably lower than that of the monopole. This is the phenomenon of the acoustic short circuit: much of the energy of the source
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
      <mi>Q</mi>
   </math>
   is absorbed by the source
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <mi>Q</mi>
   </math>
   (or inversely).
<p>
   - There is a significant difference between the near-field and far-field dipole: the amplitude of the far-field pressure increases
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>40</mn>
   </math> dB
   per decade against
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>20</mn>
   </math> dB per decade in near field (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-3.svg data-img-alt="Figure 3. Far field frequency response of the dipole. Thin line: response of the monopole.">Figure 3</a>).
<p>
   - The dipole is not omni-directional since the amplitude is proportional to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-2.svg data-img-alt="Figure 2. Directivity of the dipole (linear polar plot).">Figure 2</a>). It is said that the dipole is bidirectional.
<h3 class=sectionHead>Quadrupoles</h3>
<p>A quadrupole is made of two identical dipoles in phase opposition and placed a very small distance one from each other. As a dipole is made of two sources opposite in phase, a quadrupole is made of four monopolar sources (two in phase and two in phase opposition alternated). We distinguish the transverse quadrupole (or lateral), longitudinal quadrupole and oblique quadrupole.
<h4 class=subsectionHead>Transverse quadrupole</h4>
<p>
   The transverse quadrupole comprises four sources arranged in a square (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-4.svg data-img-alt="Figure 4. Transverse quadupole.">Figure 4</a>):
   <figure class=figure>
<p><img alt="Figure 4. Transverse quadupole." src=${config.ASSETS_URL}/01/14/theory/figure-4.svg><figcaption class=caption>Figure 4. Transverse quadupole.</figcaption></figure>
<p>
   A somewhat tedious calculation similar to that of the dipôle shows that the pressure of the transverse quadrupole assuming
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   et
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mi>r</mi>
   </math>, is given by
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jkr</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   The directivity of the transverse quadrupole is of ”four leaf clover” (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-5.svg data-img-alt="Figure 5. Directivity of the transverse quadrupole (linear polar plot).">Figure 5</a>) type and the magnitude of the pressure increases by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>60</mn>
   </math> dB per decade (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-6.svg data-img-alt="Figure 6. Far field frequency response of the transverse quadrupole. Thin lines: monopole and dipole responses.">Figure 6</a>).
   <figure class=figure>
<p><img alt="Figure 5. Directivity of the transverse quadrupole (linear polar plot)." src=${config.ASSETS_URL}/01/14/theory/figure-5.svg><figcaption class=caption>Figure 5. Directivity of the transverse quadrupole (linear polar plot).</figcaption></figure>
<figure class=figure><p><img alt="Figure 6. Far field frequency response of the transverse quadrupole. Thin lines: monopole and dipole responses." src=${config.ASSETS_URL}/01/14/theory/figure-6.svg><figcaption class=caption>Figure 6. Far field frequency response of the transverse quadrupole. Thin lines: monopole and dipole responses.</figcaption></figure>
<h4 class=subsectionHead>longitudinal quadrupole</h4>
<p>
   The longitudinal quadrupole is made of two dipoles in phase opposition and placed on a line. It therefore consists of four equationed sources, two sources in phase being superposed, the other two in phase opposition, are placed on either side of these two sources. It can also be seen as a source delivering a volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mi>Q</mi>
   </math>
   flow with two lateral sources delivering a volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <mi>Q</mi>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-7.svg data-img-alt="Figure 7. Longitudinal quadupole.">Figure 7</a>).
   <figure class=figure>
<p><img alt="Figure 7. Longitudinal quadupole." src=${config.ASSETS_URL}/01/14/theory/figure-7.svg><figcaption class=caption>Figure 7. Longitudinal quadupole.</figcaption></figure>
<p>
   The pressure radiated by the longitudinal quadrupole assuming
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <mi>r</mi>
   </math>, is given by
<table class=equation>
   <tr>
      <td style="font-size: 6px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>𝜃</mi>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jkr</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                        <msup>
                           <mrow>
                              <mi class=qopname>cos</mi>
                              <mo>⁡</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mi>𝜃</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi mathvariant=italic>jkr</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi class=qopname>cos</mi>
                              <mo>⁡</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mi>𝜃</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   The directivity of longitudinal quadrupole and of dipole resembles but it is more marked for the quadrupole (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mi>𝜃</mi>
   </math>
   instead of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>, see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-8.svg data-img-alt="Figure 8. Directivity of the longitunal quadrupole (linear polar plot).">Figure 8</a>) and as for the transverse quadrupole the amplitude in the far field pressure increases
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>60</mn>
   </math> dB per decade.
   <figure class=figure>
<p><img alt="Figure 8. Directivity of the longitunal quadrupole (linear polar plot)." src=${config.ASSETS_URL}/01/14/theory/figure-8.svg><figcaption class=caption>Figure 8. Directivity of the longitunal quadrupole (linear polar plot).</figcaption></figure>
<h4 class=subsectionHead>Oblique quadrupole</h4>
<figure class=figure>
   <p>
      <img alt="Figure 9. Oblique quadupole." src=${config.ASSETS_URL}/01/14/theory/figure-9.svg>
      <figcaption class=caption>Figure 9. Oblique quadupole.</figcaption>
</figure>
<p>
   The oblique quadrupole is made of 4 sources disposed trapezium (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-9.svg data-img-alt="Figure 9. Oblique quadupole.">Figure 9</a>). The far field pressure (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>1</mn>
   </math>) of the oblique quadrupole is given by
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
            <mi>α</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>2</mn>
      <mi>α</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>α</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-bin>−</mo>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>α</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>
   the transverse quadrupole can be decomposed into a linear combination of a transverse quadrupole and a longitudinal quadrupole. Such a quadripole can be found in a jet in which vortices combine so that to radiated like an oblique quadrupole with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mo class=MathClass-rel>≈</mo>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>6</mn>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/14/theory/figure-10.svg data-img-alt="Figure 10. Directivity of the oblique quadrupole with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi><mo class=MathClass-rel>=</mo><mi>π</mi><mo class=MathClass-bin>∕</mo><mn>6</mn></math> (linear polar plot).">Figure 10</a>).
   <figure class=figure>
<p><img alt="Figure 10. Directivity of the oblique quadrupole with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi><mo class=MathClass-rel>=</mo><mi>π</mi><mo class=MathClass-bin>∕</mo><mn>6</mn></math> (linear polar plot)." src=${config.ASSETS_URL}/01/14/theory/figure-10.svg><figcaption class=caption>Figure 10. Directivity of the oblique quadrupole with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi><mo class=MathClass-rel>=</mo><mi>π</mi><mo class=MathClass-bin>∕</mo><mn>6</mn></math> (linear polar plot).</figcaption></figure>
<h3 class=sectionHead>Decomposition of a source as a sum of multipoles</h3>
<p>
   We consider a geometrically localized source i.e. a ”compact” source. At a large distance from the source some approximations can be done. It can be shown that any ”compact” source can be decomposed into a sum of monopole, dipoles, quadrupoles, etc. Indeed, the Taylor series expansion of a scalar function of vectors
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>, assuming
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>, writes
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>f</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi mathvariant="bold">r</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>f</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msub>
               <mrow>
                  <mi mathvariant="bold">r</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>⋅</mo>
            <mtext mathvariant="bold">grad</mtext>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>f</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mo class=MathClass-punc>!</mo>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">r</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>⋅</mo>
                  <mtext mathvariant="bold">grad</mtext>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>f</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mo class=MathClass-bin>⋅</mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   This theorem can be applied to a compact set of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   point sources delivering a volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   and which positions are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>. For the pressure one obtains in the frequency domain
<table class=equation>
   <tr>
      <td style="font-size: 6px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>ω</mi>
            <mrow>
               <mo form=prefix fence=true >(</mo>
               <mrow>
                  <mi>Q</mi>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jkr</mi>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                        <mi mathvariant=italic>πr</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mo class=MathClass-open stretchy=false>(</mo>
               </mrow>
            </mrow>
            <mi mathvariant="bold">D</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mtext mathvariant="bold">grad</mtext>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mtable rowspacing=0 class=subarray-c columnalign=center>
                     <mtr>
                        <mtd>
                           <mi>i</mi>
                           <mo class=MathClass-punc>,</mo>
                           <mi>j</mi>
                           <mo class=MathClass-rel>=</mo>
                           <mn>1</mn>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>j</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
                  <mi>∂</mi>
                  <msub>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mo form=postfix fence=true>)</mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>x</mi>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>y</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>z</mi>
   </math>.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <mi class=MathClass-op>∑</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   is the total volume velocity which corresponds to the monopole component of source,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">D</mi>
      <mo class=MathClass-rel>=</mo>
      <mi class=MathClass-op>∑</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi mathvariant="bold">r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   is the first order momentum which correspond to the dipole components and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ij</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <munder class=msub>
         <mrow>
            <mi class=MathClass-op>∑</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </munder>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ni</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>nj</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   is the second order momentum which correspond to quadrupoles. This decomposition is rarely carried out in practice beyond the second order. Regardless, it is often instructive to indentify the dipolar or quadrupolar behavior of a given source. In particular, dipolar radiation is often encountered in the case of an acoustic ”short-circuited” source such as an unbaffled loudspeaker. Quadrupole sources are found in aero-acoustics in particular in the case of vortex noise (jet noise in particular).
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
<dt>[1]
<dd> L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
<dt>[2]
<dd> M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
<dt>[3]
<dd> A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
<dt>[4]
<dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
<dt>[5]
<dd> E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
<dt>[6]
<dd> S. Temkin. Elements of Acoustics. Wiley, New York, 1981.`;

export default theory;
